<template>
  <div class="grid grid-cols-2 items-center py-2">
    <div class="my-auto text-sm font-normal text-gray-700">{{ label }}</div>
    <div class="color-picker">
      <HLContentWrap class="hl-color-picker-wrap">
        <HLColorPicker
          :value="defaultValue"
          :swatches="swatches"
          :forward-picker-updates="true"
          :picker-options="{ showCTA: true }"
          :content-style="COLOR_PICKER_POPOVER_STYLE"
          @swatch:add="handleSwatchAdd"
          @swatch:edit="handleSwatchEdit"
          @swatch:delete="handleSwatchDelete"
          @update:picker="handlePickerUpdate"
          @update:picker:back="handlePickerBack"
          @update:picker:close="handlePickerClose"
          @select="handleSelect"
        >
          <template #swatchAddButton="{ sectionKey, sectionIndex }">
            <HLButton
              v-if="sectionKey === 'custom'"
              :id="`${sectionKey}-${sectionIndex}`"
              color="blue"
              variant="secondary"
              text
              class="p-0"
              @click="onSwatchAdd"
            >
              <Settings01Icon class="mr-1 h-3 w-3" />
              {{ $t("common.manage") }}
            </HLButton>
          </template>
        </HLColorPicker>
      </HLContentWrap>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useColorPickerSwatchCrudHandlers } from "@/composition/useColorPickerSwatchCrudHandlers";
import { useColorPickerSwatches } from "@/composition/useColorPickerSwatches";
import { useColorPickerValueHandlers } from "@/composition/useColorPickerValueHandlers";
import { useColorResolver } from "@/composition/useColorResolver";
import { useAppStore } from "@/store/app";
import { toUpperHex, toUpperHexWithHash } from "@/util/color";
import { COLOR_PICKER_POPOVER_STYLE } from "@/util/colorPicker";
import { Settings01Icon } from "@gohighlevel/ghl-icons/24/outline";
import { HLButton, HLColorPicker, HLContentWrap } from "@platform-ui/highrise";
import { ref, watch } from "vue";
import { useI18n } from "vue-i18n";

const { t } = useI18n();

const props = defineProps({
  label: {
    type: String,
    default: "",
  },
  color: {
    type: String,
    default: "FFFFFF",
  },
});

const emit = defineEmits(["update:color"]);

const store = useAppStore();
const { resolveHexFromProp } = useColorResolver(store);

const defaultValue = ref<string>(toUpperHexWithHash(resolveHexFromProp(props.color)));

const applyColorValue = (colorValue: string) => {
  defaultValue.value = toUpperHexWithHash(colorValue);
  emit("update:color", toUpperHex(colorValue));
};

const { swatches, onSwatchAdd, handleDefaultSwatchAdd } = useColorPickerSwatches(store, {
  t,
  onApplyColor: applyColorValue,
});

const { handleSelect, handlePickerUpdate, handlePickerBack, handlePickerClose } =
  useColorPickerValueHandlers({
    getCommittedColor: () => resolveHexFromProp(props.color),
    onColor: applyColorValue,
    onSelect: (hex) => store.recordBrandColorPickFromHex(hex),
  });

const { handleSwatchAdd, handleSwatchEdit, handleSwatchDelete } = useColorPickerSwatchCrudHandlers(
  store,
  {
    onCustomAdd: onSwatchAdd,
    onDefaultAdd: handleDefaultSwatchAdd,
    logPrefix: "ColorInputField",
  }
);

watch(
  () => props.color,
  (newColor) => (defaultValue.value = toUpperHexWithHash(resolveHexFromProp(newColor)))
);
</script>

<style scoped>
.color-picker {
  display: flex;
  align-items: center;
}
</style>
